import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_CONFERENCE_CONFLICTS_URL,
  DASHBOARD,
  getAdminResolveConflictUrl,
} from '../constants/routes';
import AuthService from '../services/authservices';
import Spinner from '../components/Spinner';

type Conflict = {
  id: string;
  sport_code: string;
  season: number;
  sc_match_id: string;
  team_code_subject: string;
  team_code_counterpart: string;
  team_name_subject: string;
  team_name_counterpart: string;
  game_date: string | null;
  flat_match_column: string;
  value_subject: string | null;
  value_counterpart: string | null;
  detected_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_value: string | null;
};

type ConflictGroup = {
  sc_match_id: string;
  sport_code: string;
  season: number;
  game_date: string | null;
  team_name_subject: string;
  team_name_counterpart: string;
  conflicts: Conflict[];
};

const authService = new AuthService();

const COLUMN_LABELS: Record<string, string> = {
  conference_name: 'Conference',
  conference_international_name: 'Conference (International)',
  opponent_conference_name: 'Opponent Conference',
  opponent_conference_international_name: 'Opponent Conference (International)',
};

export default function ConferenceConflictsPage() {
  const [groups, setGroups] = useState<ConflictGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [sport, setSport] = useState<string>('');
  const [season, setSeason] = useState<string>('');
  const [workingId, setWorkingId] = useState<string | null>(null);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    if (sport.trim()) params.set('sport', sport.trim());
    if (season.trim()) params.set('season', season.trim());
    return params.toString();
  }, [sport, season]);

  const groupConflicts = useCallback((conflicts: Conflict[]): ConflictGroup[] => {
    const map = new Map<string, ConflictGroup>();
    conflicts.forEach((c) => {
      const existing = map.get(c.sc_match_id);
      if (existing) {
        existing.conflicts.push(c);
      } else {
        map.set(c.sc_match_id, {
          sc_match_id: c.sc_match_id,
          sport_code: c.sport_code,
          season: c.season,
          game_date: c.game_date,
          team_name_subject: c.team_name_subject,
          team_name_counterpart: c.team_name_counterpart,
          conflicts: [c],
        });
      }
    });
    return Array.from(map.values());
  }, []);

  const loadConflicts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${ADMIN_CONFERENCE_CONFLICTS_URL}${query ? `?${query}` : ''}`;
      const res = await authService.authenticatedFetch(url);
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to load conflicts');
      const data = (await res.json()) as Conflict[];
      setGroups(groupConflicts(data));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [query, groupConflicts]);

  useEffect(() => {
    void loadConflicts();
  }, [loadConflicts]);

  const handleResolve = async (conflictId: string, value: string) => {
    setWorkingId(conflictId);
    setMessage(null);
    setError(null);
    try {
      const res = await authService.authenticatedFetch(getAdminResolveConflictUrl(conflictId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resolution_value: value }),
      });
      const data = (await res.json().catch(() => ({}))) as { detail?: string };
      if (!res.ok) throw new Error(data.detail || 'Resolve failed');
      setMessage('Conflict resolved.');
      await loadConflicts();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Resolve failed');
    } finally {
      setWorkingId(null);
    }
  };

  const formatValue = (v: string | null) => (v == null || v.trim() === '' ? <em className="text-slate-400">(empty)</em> : v);

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto max-w-7xl space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-900">Conference Conflicts</h1>
          <a href={DASHBOARD} className="text-sm text-cyan-700 underline">
            Back to dashboard
          </a>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-slate-700">Sport</label>
            <input
              type="text"
              className="w-24 rounded border px-2 py-1 text-sm"
              value={sport}
              onChange={(e) => setSport(e.target.value)}
              placeholder="MFB"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-slate-700">Season</label>
            <input
              type="number"
              className="w-28 rounded border px-2 py-1 text-sm"
              value={season}
              onChange={(e) => setSeason(e.target.value)}
              placeholder="2024"
            />
          </div>
          <button
            onClick={() => void loadConflicts()}
            className="rounded bg-cyan-700 px-3 py-1 text-sm font-medium text-white hover:bg-cyan-800"
          >
            Refresh
          </button>
        </div>

        {message && <div className="rounded bg-green-100 p-3 text-sm text-green-800">{message}</div>}
        {error && <div className="rounded bg-red-100 p-3 text-sm text-red-800">{error}</div>}

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        )}

        {!loading && groups.length === 0 && (
          <div className="rounded border bg-white p-6 text-sm text-slate-600">No unresolved conference conflicts.</div>
        )}

        {!loading &&
          groups.map((g) => (
            <div key={g.sc_match_id} className="rounded border bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    {g.team_name_subject} vs {g.team_name_counterpart}
                  </h2>
                  <p className="text-sm text-slate-500">
                    {g.sport_code} · Season {g.season}
                    {g.game_date ? ` · ${new Date(g.game_date).toLocaleDateString()}` : ''}
                    {` · Match ${g.sc_match_id}`}
                  </p>
                </div>
              </div>

              <table className="w-full text-left text-sm">
                <thead className="bg-slate-100 text-slate-700">
                  <tr>
                    <th className="px-3 py-2">Field</th>
                    <th className="px-3 py-2">{g.team_name_subject}</th>
                    <th className="px-3 py-2">{g.team_name_counterpart}</th>
                    <th className="px-3 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {g.conflicts.map((c) => {
                    const isWorking = workingId === c.id;
                    return (
                      <tr key={c.id} className="border-t">
                        <td className="px-3 py-2 font-medium">
                          {COLUMN_LABELS[c.flat_match_column] || c.flat_match_column}
                        </td>
                        <td className="px-3 py-2">{formatValue(c.value_subject)}</td>
                        <td className="px-3 py-2">{formatValue(c.value_counterpart)}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <button
                              disabled={isWorking}
                              onClick={() => void handleResolve(c.id, c.value_subject || '')}
                              className="rounded border border-cyan-700 px-2 py-1 text-xs text-cyan-700 hover:bg-cyan-50 disabled:opacity-50"
                            >
                              Use {g.team_name_subject}
                            </button>
                            <button
                              disabled={isWorking}
                              onClick={() => void handleResolve(c.id, c.value_counterpart || '')}
                              className="rounded border border-cyan-700 px-2 py-1 text-xs text-cyan-700 hover:bg-cyan-50 disabled:opacity-50"
                            >
                              Use {g.team_name_counterpart}
                            </button>
                            {isWorking && <Spinner size="xs" />}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
      </div>
    </div>
  );
}
